// src/App.js

import React, { useState, useEffect, useContext, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import Header from "./components/Common/Header";
import MainContent from "./components/Search/MainContent";
import Footer from "./components/Common/Footer";
import Login from "./components/Authentication/Login";
import RoleSelection from "./components/Authentication/RoleSelection";
import AdminPanel from "./components/Admin/AdminPanel";
import UserProfile from "./components/Authentication/UserProfile";
import FavoritesPage from "./components/Search/FavoritesPage";
import { AuthContext, AuthProvider } from "./contexts/AuthContext";
import { ResultsProvider } from "./contexts/ResultsContext";
import { ArrowUpIcon } from "@heroicons/react/24/solid";
import Particles from "./components/Common/Particles";
import "./index.css";
import "./components/Common/NotFound/NotFound.css";
import ErrorBoundary from "./components/Common/ErrorBoundary";
import ProtectedRoute from "./components/Common/ProtectedRoute";
import { ToastProvider, ToastContext } from "./contexts/ToastContext";
import NotFound from "./components/Common/NotFound/NotFound";

function AppContent() {
  const { isAuthenticated, username } = useContext(AuthContext);
  const location = useLocation();
  const { addToast } = useContext(ToastContext);
  const toastShownRef = useRef(false);
  const [showGoUp, setShowGoUp] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Do not show footer on login and 404 pages
  const shouldShowFooter = !["/login", "/404"].includes(location.pathname);

  // Toast Notification for Greeting User
  useEffect(() => {
    if (isAuthenticated && username && !toastShownRef.current) {
      addToast(`Hello, ${username}! Welcome!`, "success");
      toastShownRef.current = true;
    }
  }, [isAuthenticated, username, addToast]);

  useEffect(() => {
    // Prevent Access to Developer Tools via Keyboard Shortcuts
    const handleKeyDown = (e) => {
      // F12
      if (e.keyCode === 123) {
        e.preventDefault();
      }
      // Ctrl+Shift+I
      if (e.ctrlKey && e.shiftKey && e.keyCode === 73) {
        e.preventDefault();
      }
      // Ctrl+Shift+J
      if (e.ctrlKey && e.shiftKey && e.keyCode === 74) {
        e.preventDefault();
      }
      // Ctrl+U
      if (e.ctrlKey && e.keyCode === 85) {
        e.preventDefault();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    // Detect Developer Tools and Close/Redirect Application
    const detectDevTools = () => {
      const threshold = 160; // Time threshold in milliseconds
      const start = performance.now();
      // The debugger statement will pause execution if DevTools is open
      debugger;
      const end = performance.now();
      if (end - start > threshold) {
        // Developer Tools detected
        window.close(); // or:
        window.location.href = "about:blank";
      }
    };

    // Check every 1 second
    const interval = setInterval(detectDevTools, 1000);

    // Cleanup event listeners and interval
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      clearInterval(interval);
    };
  }, []);

  // Implement scroll listener for Scroll-to-Top button
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowGoUp(true);
      } else {
        setShowGoUp(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-primary to-secondary text-white relative">
      {/* Main content area */}
      <main className="flex-grow relative overflow-hidden">
        <Routes>
          {/* Role Selection Route */}
          <Route
            path="/role-selection"
            element={
              <ProtectedRoute requiredRole="Admin">
                <RoleSelection />
              </ProtectedRoute>
            }
          />

          {/* Admin Panel Route */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRole="Admin">
                <AdminPanel />
              </ProtectedRoute>
            }
          />

          {/* Login Route */}
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
          />

          {/* Profile Route */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute requiredRole="User">
                <UserProfile />
              </ProtectedRoute>
            }
          />

          {/* Favorites Route */}
          <Route
            path="/favorites"
            element={
              <ProtectedRoute>
                <Header />
                <FavoritesPage />
              </ProtectedRoute>
            }
          />

          {/* Main Page Route */}
          <Route
            path="/"
            element={
              <ProtectedRoute allowGuest={true}>
                <>
                  {/* Particle Effects */}
                  <Particles count={40} />

                  {/* Display main content */}
                  <Header />
                  <MainContent />
                </>
              </ProtectedRoute>
            }
          />

          {/* 404 Page Route */}
          <Route path="/404" element={<NotFound />} />

          {/* Redirect to /404 for unknown routes */}
          <Route path="*" element={<Navigate to="/404" />} />
        </Routes>
      </main>

      {/* Conditionally Render Footer */}
      {shouldShowFooter && <Footer />}

      {/* Go to top button */}
      {showGoUp && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-10 right-10 bg-accentBlue text-white rounded-full p-3 shadow-lg transition-opacity duration-300 opacity-90 hover:opacity-100 focus:outline-none z-50"
          aria-label="Go to top"
        >
          <ArrowUpIcon className="h-6 w-6" />
        </button>
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <ResultsProvider>
        <ToastProvider>
          <ErrorBoundary>
            <Router>
              <AppContent />
            </Router>
          </ErrorBoundary>
        </ToastProvider>
      </ResultsProvider>
    </AuthProvider>
  );
}

export default App;
